import i18n from 'i18next';
import {v4 as uuid} from 'uuid';

import type {
    BreakoutReferDetails,
    DisconnectReason,
    ExtendedInfinityErrorCode,
    ExtendedInfinityErrorMessage,
    InfinityClient,
    Layout,
    ParticipantID,
    RoomID,
    SplashScreen,
    Stats,
    TransferDetails,
} from '@pexip/infinity';
import {
    MAIN_ROOM_ID,
    createInfinityClient,
    isControlOnly,
    isReceivingAnyMedia,
    isReceivingPresentation,
    isSendingPresentation,
} from '@pexip/infinity';
import type {MediaTrack} from '@pexip/media';
import {memoize} from '@pexip/utils';
import type {Detach, Signal} from '@pexip/signal';
import type {
    ChatMessage,
    DirectChatMessages,
    UnseenUnreadDirectChatMessages,
    UserChatMessage,
    BreakoutAskingForHelpStatus,
} from '@pexip/media-components';
import {
    browserVersion,
    currentBrowserName,
    MainBreakoutRoomId,
} from '@pexip/media-components';

import {callSignals} from '../signals/Call.signals';
import {
    participantSearchQuerySignal,
    userInitiatedDisconnectSignal,
} from '../signals/InMeeting.signals';
import {infinityClientSignals} from '../signals/InfinityClient.signals';
import {config} from '../config';
import {applicationConfig} from '../applicationConfig';
import {CLIENT_ID, CHARACTER_LIMIT} from '../constants';
import {logger} from '../logger';
import {toCallRecent} from '../utils/toCallRecent';
import type {
    Idp,
    LiveCaption,
    ParticipantActivity,
    GetSignalType,
} from '../types';
import {MeetingFlow} from '../types';
import {navigateToPostMeeting} from '../router';
import {
    chatMessageSignal,
    chatMessagesSignal,
    directChatMessagesSignal,
    endPresentationSignal,
    guestTransferDetailsSignal,
    hostReferDetailsSignal,
    idpSignal,
    infinityErrorSignal,
    invalidPinSignal,
    layoutOverlayTextEnabledSignal,
    liveCaptionsEnabledSignal,
    selectedCaptionsLanguageSignal,
    isTogglingLiveCaptionsSignal,
    meetingSignal,
    outgoingMessageSignal,
    pinRequiredSignal,
    presentationStreamSignal,
    remoteStreamSignal,
    splashScreenSignal,
    startPresentationSignal,
    stepSignal,
    liveCaptionsSignal,
    transferSignal,
    unreadChatMessagesSignal,
    unreadDirectChatMessagesSignal,
    unseenUnreadDirectChatMessagesSignal,
    breakoutsAskingForHelpSignal,
    breakoutHelpRequestedSignal,
    breakoutHelpCancelledSignal,
} from '../signals/Meeting.signals';
import {mediaSignals} from '../signals/Media.signals';
import {participantActivityBatchedSignal} from '../signals/Participant.signals';
import {
    GroupKey,
    createInMeetingParticipants,
    type RoomParticipantMap,
} from '../utils/createParticipants';
import {createSignalState, type SignalState} from '../utils/createSignalState';
import {isAllowedToPresent} from '../utils/isAllowedToPresent';

import {mediaService} from './Media.service';
import {callHistoryService} from './CallHistory.service';
import {registrationService} from './Registration.service';

type CallParams = Parameters<InfinityClient['call']>[0];
type CallArgs = Omit<
    CallParams,
    'bandwidth' | 'callType' | 'clientId' | 'displayName' | 'node'
>;

type EndArgs = Parameters<InfinityClient['disconnect']>[0];
type BreakoutRoomDetail = Parameters<InfinityClient['breakout']>[0];

const subscribePageHide = (handler: (event: Event) => void) => {
    window.addEventListener('pagehide', handler);
    return () => {
        window.removeEventListener('pagehide', handler);
    };
};

type ExtractSignalType<T extends Signal<unknown>> =
    T extends Signal<infer S> ? S : never;

// biome-ignore lint/suspicious/noExplicitAny: necessary to override a primitive
type AsyncFunc = (...args: any[]) => Promise<any>;

const toFn =
    <F extends AsyncFunc>(fn: F) =>
    (...args: Parameters<F>) => {
        void fn(...args);
    };

type CallAttributes = Pick<
    CallArgs,
    | 'conferenceAlias'
    | 'callTag'
    | 'conferenceExtension'
    | 'mediaStream'
    | 'token'
>;
type LayoutTransforms = Parameters<
    InfinityClient['setLayout']
>[0]['transforms'];

export enum CallStage {
    New = 0,
    EventStreamConnected = 1,
    Connected = 2,
    Restarting = 3,
    Ending = 4,
    Ended = 5,
}
type InfinityErrorInfo = {
    type: ExtendedInfinityErrorMessage;
    code: ExtendedInfinityErrorCode;
};

export interface MeetingAttributes {
    step: MeetingFlow;
    idps: Idp[];
    liveCaptionsEnabled: boolean;
    selectedCaptionsLanguage: string;
    isTogglingLiveCaptions: boolean;
    remoteStream?: MediaStream | undefined;
    presentationStream?: MediaStream | undefined;
    pinRequired: boolean;
    detachSignals: Detach[];
    splashScreen?: SplashScreen;
    callStage: CallStage;
    endedReason?: DisconnectReason;
    layoutOverlayTextEnabled: boolean;
    error?: InfinityErrorInfo;
    joinAsHost?: boolean;
    currentHostLayout: Layout;
    chatMessages: ChatMessage[];
    unreadChatMessages: ChatMessage[];
    unseenUnreadDirectChatMessages: UnseenUnreadDirectChatMessages;
    directChatMessages: DirectChatMessages;
    unreadDirectChatMessages: DirectChatMessages;
    liveCaptions: LiveCaption[];
    previousParticipants: RoomParticipantMap;
    previousParticipantActivities: ParticipantActivity[];
    isPresentationInMixActive: boolean;
    searchQuery?: string;
}
interface MeetingProps
    extends
        CallAttributes,
        Omit<
            MeetingAttributes,
            'previousParticipants' | 'previousParticipantActivities'
        > {
    participants: ReturnType<typeof createInMeetingParticipants>;
    currentFeccPids: SignalState<Set<ParticipantID>>;
    breakoutsAskingForHelp: BreakoutAskingForHelpStatus[];
    guestTransferDetails: TransferDetails | undefined;
    hostReferDetails: BreakoutReferDetails | undefined;
}
interface MeetingControls {
    call(args: Partial<CallArgs>): Promise<void>;
    endCall(args: EndArgs): Promise<void>;
    restartCall(args: CallArgs): Promise<void>;
    changeLayout(transforms: LayoutTransforms): Promise<boolean>;
    changePersonalLayout(transforms: LayoutTransforms): Promise<boolean>;
}

const deriveDegradationPreference = (): RTCDegradationPreference => {
    const hint = config.get('presoContentHint');
    switch (hint) {
        case 'motion':
            return 'maintain-framerate';
        case 'detail':
        case 'text':
            return 'maintain-resolution';
        default:
            return 'balanced';
    }
};

type CommonCallConfigs = Pick<
    CallParams,
    | 'allowChangeOfMediaDirection'
    | 'allowH264'
    | 'bandwidth'
    | 'callType'
    | 'clientId'
    | 'degradationPreference'
    | 'directMedia'
    | 'displayName'
    | 'encodingPriorities'
    | 'networkPriorities'
    | 'node'
    | 'supportsDirectChat'
    | 'jitterBufferTarget'
    | 'allowVP9'
    | 'maxFrameSize'
>;

const getCallConfigs = (): CommonCallConfigs => ({
    bandwidth: Number(config.get('bandwidth')),
    callType: config.get('callType'),
    allowChangeOfMediaDirection: config.get('allowChangeOfMediaDirection'),
    clientId: CLIENT_ID,
    directMedia: applicationConfig.directMedia,
    displayName:
        (config.get('skipDisplayNamePrompt') && config.get('displayName')) ||
        false,
    node: applicationConfig.node,
    networkPriorities: applicationConfig.networkPriorities,
    encodingPriorities: applicationConfig.encodingPriorities,
    degradationPreference: {presentation: deriveDegradationPreference()},
    jitterBufferTarget: applicationConfig.jitterBufferTarget,
    supportsDirectChat: true,
    allowH264:
        !(currentBrowserName === 'Safari Mac') ||
        ![18.4, 18.5].includes(browserVersion),
    allowVP9: config.get('allowVP9'),
    maxFrameSize: config.get('maxFrameSize'),
});

type MuteStack = {
    timerId: number;
    muted: undefined | boolean;
};
const createDebounceMute =
    (stack: MuteStack, handle: (muted: boolean) => void, throttleMS = 800) =>
    (track: MediaTrack) => {
        if (stack.timerId) {
            window.clearTimeout(stack.timerId);
            stack.timerId = 0;
        }
        if (stack.muted === undefined) {
            stack.muted = track.muted ?? true;
        } else {
            if (stack.muted !== (track.muted ?? true)) {
                stack.muted = undefined;
            }
        }
        stack.timerId = window.setTimeout(() => {
            if (stack.muted === undefined) {
                return;
            }
            handle(stack.muted);
            stack.muted = undefined;
        }, throttleMS);
    };

const createMeeting = ({
    infinity,
    callAttrs,
    meetingAttrs = {},
    controls,
}: {
    infinity: InfinityClient;
    callAttrs: CallAttributes;
    meetingAttrs?: Partial<MeetingAttributes>;
    controls: MeetingControls | undefined;
}) => {
    const props: MeetingProps = {
        ...callAttrs,
        idps: [],
        liveCaptionsEnabled: meetingAttrs.liveCaptionsEnabled ?? false,
        selectedCaptionsLanguage: meetingAttrs.selectedCaptionsLanguage ?? '',
        isTogglingLiveCaptions: false,
        step: MeetingFlow.Loading,
        pinRequired: false,
        callStage: CallStage.New,
        endedReason: undefined,
        layoutOverlayTextEnabled: false,
        detachSignals: [],
        currentHostLayout: undefined,
        chatMessages: meetingAttrs.chatMessages ?? [],
        unreadChatMessages: meetingAttrs.unreadChatMessages ?? [],
        directChatMessages: meetingAttrs.directChatMessages ?? new Map(),
        unreadDirectChatMessages:
            meetingAttrs.unreadDirectChatMessages ?? new Map(),
        unseenUnreadDirectChatMessages:
            meetingAttrs.unseenUnreadDirectChatMessages ?? [],
        liveCaptions: [],
        participants: createInMeetingParticipants(
            infinity,
            () => mediaService.media,
            infinityClientSignals,
            participantActivityBatchedSignal,
            {
                participants: meetingAttrs.previousParticipants,
                activities: meetingAttrs.previousParticipantActivities,
            },
        ),
        currentFeccPids: createSignalState(new Set()),
        isPresentationInMixActive: false,
        searchQuery: '',
        breakoutsAskingForHelp: [],
        guestTransferDetails: undefined,
        hostReferDetails: undefined,
    };

    const createSignalHandler = <T extends Signal<unknown>>(
        signal: T,
        handle: (arg: ExtractSignalType<T>) => void | Promise<void>,
        name = 'event',
    ) => {
        return async (arg: ExtractSignalType<T>) => {
            try {
                await handle(arg);
                logger.info(
                    {context: 'Meeting Signals', signal: signal.name},
                    `Handled ${signal.name ?? name}`,
                );
            } catch (error) {
                logger.error(
                    {error},
                    `Failed to handle ${signal.name ?? name}`,
                );
            }
        };
    };

    const updateIdp = (idps: Idp[]) => {
        if (props.idps === idps) {
            return;
        }
        props.idps = idps;
        idpSignal.emit();
        updateStep(MeetingFlow.Idp);
    };

    const updateSearchQuery = (searchQuery: string) => {
        if (props.searchQuery === searchQuery) {
            return;
        }
        props.searchQuery = searchQuery;
        participantSearchQuerySignal.emit();
    };

    const uppdateLiveCaptionsEnabled = (enabled: boolean) => {
        if (props.liveCaptionsEnabled === enabled) {
            return;
        }
        props.liveCaptionsEnabled = enabled;
        liveCaptionsEnabledSignal.emit();
    };

    const updateSelectedCaptionsLanguage = (language: string) => {
        if (props.selectedCaptionsLanguage === language) {
            return;
        }
        props.selectedCaptionsLanguage = language;
        selectedCaptionsLanguageSignal.emit();
    };

    const updateIsTogglingLiveCaptions = (isToggling: boolean) => {
        if (props.isTogglingLiveCaptions === isToggling) {
            return;
        }
        props.isTogglingLiveCaptions = isToggling;
        isTogglingLiveCaptionsSignal.emit();
    };

    const toggleLiveCaptions = () => {
        // Ignore toggles while a request is still in flight
        if (props.isTogglingLiveCaptions) {
            return;
        }
        const enable = !props.liveCaptionsEnabled;
        updateIsTogglingLiveCaptions(true);
        enableLiveCaptions(
            {enable},
            () => {
                uppdateLiveCaptionsEnabled(enable);
                updateIsTogglingLiveCaptions(false);
            },
            () => {
                updateIsTogglingLiveCaptions(false);
            },
        );
    };

    const updateLayoutOverLayTextEnabled = (enabled: boolean) => {
        if (props.layoutOverlayTextEnabled === enabled) {
            return;
        }
        props.layoutOverlayTextEnabled = enabled;
        layoutOverlayTextEnabledSignal.emit();
    };

    const updateStep = (newStep: MeetingFlow) => {
        if (props.step === newStep) {
            return;
        }
        props.step = newStep;
        logger.debug(
            {context: 'Meeting', meetingStage: MeetingFlow[props.step]},
            'Update Meeting stage',
        );
        stepSignal.emit(newStep);
    };

    const updateSplashScreen = (splashScreen: SplashScreen | undefined) => {
        if (props.splashScreen === splashScreen) {
            return;
        }
        props.splashScreen = splashScreen;
        splashScreenSignal.emit();
    };

    const updateBreakoutsAskingForHelp = (
        statuses: BreakoutAskingForHelpStatus[],
    ) => {
        props.breakoutsAskingForHelp = statuses;
        breakoutsAskingForHelpSignal.emit(statuses);
    };

    const updateGuestTransferDetails = (
        details: TransferDetails | undefined,
    ) => {
        if (props.guestTransferDetails === details) {
            return;
        }
        props.guestTransferDetails = details;
        guestTransferDetailsSignal.emit();
    };

    const updateHostReferDetails = (
        details: BreakoutReferDetails | undefined,
    ) => {
        if (props.hostReferDetails === details) {
            return;
        }
        props.hostReferDetails = details;
        hostReferDetailsSignal.emit();
    };

    const updateInfinityError = (
        error: ExtendedInfinityErrorMessage,
        errorCode: ExtendedInfinityErrorCode,
    ) => {
        void end({reason: error});
        props.error = {type: error, code: errorCode};
        infinityErrorSignal.emit(props.error);
    };

    const updateChatMessages = (chatMessages: ChatMessage[]) => {
        if (props.chatMessages === chatMessages) {
            return;
        }
        props.chatMessages = chatMessages;
        chatMessagesSignal.emit();
    };

    const updateUnreadChatMessages = (chatMessages: ChatMessage[]) => {
        if (props.unreadChatMessages === chatMessages) {
            return;
        }
        props.unreadChatMessages = chatMessages;
        unreadChatMessagesSignal.emit();
    };

    const getDirectChatMessagesWithParticipant = (
        withParticipantID: ParticipantID,
    ) => props.directChatMessages.get(withParticipantID);

    const updateDirectChatMessages = (
        directChatMessages: DirectChatMessages,
    ) => {
        if (props.directChatMessages === directChatMessages) {
            return;
        }
        props.directChatMessages = directChatMessages;
        directChatMessagesSignal.emit();
    };

    const addDirectChatMessage = (
        withParticipantID: ParticipantID,
        chatMessage: UserChatMessage,
    ) => {
        const currentMessagesWithParticipant =
            getDirectChatMessagesWithParticipant(withParticipantID) ?? [];
        const newDirectChatMessages = new Map(props.directChatMessages);
        newDirectChatMessages.delete(withParticipantID); // delete to set new map order so that we can loop over latest messages first
        newDirectChatMessages.set(withParticipantID, [
            ...currentMessagesWithParticipant,
            {...chatMessage},
        ]);
        updateDirectChatMessages(newDirectChatMessages);
    };

    const deleteDirectChatMessage = (
        withParticipantID: ParticipantID,
        chatMessageIDToDelete: string,
    ) => {
        if (!props.directChatMessages.has(withParticipantID)) {
            return;
        }
        const currentMessagesWithParticipant =
            getDirectChatMessagesWithParticipant(withParticipantID) ?? [];
        const newDirectChatMessagesWithParticipant =
            currentMessagesWithParticipant.filter(
                message => message.id !== chatMessageIDToDelete,
            );
        const newDirectChatMessages = new Map(props.directChatMessages);
        newDirectChatMessages.set(
            withParticipantID,
            newDirectChatMessagesWithParticipant,
        );
        updateDirectChatMessages(newDirectChatMessages);
    };

    const getUnreadDirectChatMessagesWithParticipant = (
        withParticipantID: ParticipantID,
    ) => props.unreadDirectChatMessages.get(withParticipantID);

    const updateUnreadDirectChatMessages = (
        directChatMessages: DirectChatMessages,
    ) => {
        if (props.unreadDirectChatMessages === directChatMessages) {
            return;
        }
        props.unreadDirectChatMessages = directChatMessages;
        unreadDirectChatMessagesSignal.emit();
    };

    const addUnseenUnreadDirectChatMessage = (messageID: string) => {
        props.unseenUnreadDirectChatMessages = [
            ...props.unseenUnreadDirectChatMessages,
            messageID,
        ];
        unseenUnreadDirectChatMessagesSignal.emit();
    };

    const deleteAllUnseenUnreadDirectChatMessages = () => {
        if (props.unseenUnreadDirectChatMessages.length > 0) {
            props.unseenUnreadDirectChatMessages = [];
            unseenUnreadDirectChatMessagesSignal.emit();
        }
    };

    const deleteUnseenUnreadDirectChatMessages = (messageIDs: string[]) => {
        const newUnseenUnreadDirectChatMessages =
            props.unseenUnreadDirectChatMessages.filter(
                id => !messageIDs.includes(id),
            );
        if (
            newUnseenUnreadDirectChatMessages.length !==
            props.unseenUnreadDirectChatMessages.length
        ) {
            props.unseenUnreadDirectChatMessages =
                newUnseenUnreadDirectChatMessages;
            unseenUnreadDirectChatMessagesSignal.emit();
        }
    };

    const deleteAllUnseenUnreadDirectChatMessagesWithParticipant = (
        withParticipantID: ParticipantID,
    ) => {
        const unreadMessages =
            getUnreadDirectChatMessagesWithParticipant(withParticipantID) ?? [];
        deleteUnseenUnreadDirectChatMessages(unreadMessages.map(({id}) => id));
    };

    const addUnreadDirectChatMessage = (
        withParticipantID: ParticipantID,
        chatMessage: UserChatMessage,
    ) => {
        const unreadMessages =
            getUnreadDirectChatMessagesWithParticipant(withParticipantID) ?? [];
        const newUnreadDirectChatMessages = new Map(
            props.unreadDirectChatMessages,
        );
        newUnreadDirectChatMessages.delete(withParticipantID); // reorder map
        newUnreadDirectChatMessages.set(withParticipantID, [
            ...unreadMessages,
            {...chatMessage},
        ]);
        updateUnreadDirectChatMessages(newUnreadDirectChatMessages);
        addUnseenUnreadDirectChatMessage(chatMessage.id);
    };

    const deleteUnreadDirectChatMessagesWithParticipant = (
        withParticipantID: ParticipantID,
    ) => {
        const newUnreadDirectChatMessages = new Map(
            props.unreadDirectChatMessages,
        );
        deleteAllUnseenUnreadDirectChatMessagesWithParticipant(
            withParticipantID,
        );
        newUnreadDirectChatMessages.delete(withParticipantID);
        updateUnreadDirectChatMessages(newUnreadDirectChatMessages);
    };

    const setRemoteStream = (remoteStream: MediaStream) => {
        if (props.remoteStream === remoteStream) {
            return;
        }
        props.remoteStream = remoteStream;
        remoteStreamSignal.emit(remoteStream);
    };

    const setPinRequired = (pinRequired: boolean) => {
        if (props.pinRequired === pinRequired) {
            return;
        }
        props.pinRequired = pinRequired;
        pinRequiredSignal.emit(pinRequired);
    };

    const release = () => {
        for (const d of props.detachSignals) {
            d();
        }
        props.participants.release();
        props.detachSignals.length = 0;
    };

    const continueCall = async (args: Partial<CallArgs>) => {
        if (!controls) {
            return;
        }
        updateStep(MeetingFlow.Loading);
        await controls.call(args);
    };

    const end = async (
        args: EndArgs = {reason: 'User initiated disconnect'},
    ) => {
        if (props.callStage >= CallStage.Ending) {
            return;
        }
        props.endedReason = args.reason;
        try {
            props.callStage = CallStage.Ending;
            if (controls) {
                await controls.endCall(args);
                if (args.reason === 'User initiated disconnect') {
                    userInitiatedDisconnectSignal.emit();
                }
            }
        } finally {
            release();
            props.callStage = CallStage.Ended;
        }
    };

    const leave = async () => {
        try {
            updateStep(MeetingFlow.Loading);
            await end();
        } finally {
            navigateToPostMeeting(
                props.conferenceAlias,
                applicationConfig.disconnectDestination,
            );
        }
    };

    const present = (stream: MediaStream) => {
        infinity.present(stream);
    };
    const endPresent = () => {
        if (!controls) {
            return;
        }
        infinity.stopPresenting();
        endPresentationSignal.emit();
    };
    const raiseHand = async (
        raise: boolean,
        participantUuid?: ParticipantID,
    ) => {
        await infinity.raiseHand({raise, participantUuid});
    };
    const lowerAllRaisedHands = async () => {
        await infinity.lowerAllRaisedHands({});
    };
    const _changeLayout = async (transforms: LayoutTransforms) => {
        try {
            return await controls?.changeLayout(transforms);
        } catch (error: unknown) {
            if (!(error instanceof Error)) {
                throw error;
            }
            logger.error(
                {context: 'Meeting', error, transforms},
                'failed to change layout',
            );
        }
    };
    const changeLayout = async (
        layout: Layout,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await _changeLayout({
                layout,
                guest_layout:
                    infinity.serviceType === 'lecture' ? layout : undefined,
            });
        } catch (error: unknown) {
            onFail?.(error);
        } finally {
            onDone?.();
        }
    };
    const _changePersonalLayout = async (transforms: LayoutTransforms) => {
        try {
            return await controls?.changePersonalLayout(transforms);
        } catch (error: unknown) {
            if (!(error instanceof Error)) {
                throw error;
            }
            logger.error(
                {context: 'Meeting', error, transforms},
                'failed to change layout',
            );
        }
    };
    const changePersonalLayout = async (
        layout: Layout,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await _changePersonalLayout({
                layout,
            });
        } catch (error: unknown) {
            onFail?.(error);
        } finally {
            onDone?.();
        }
    };

    const resetToMeetingLayout = async (
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await infinity.deleteVideoMix({});
            onDone?.();
        } catch (error: unknown) {
            onFail?.(error);
        }
    };

    const _breakoutRequestGuestToken = async (breakoutUuid: RoomID) => {
        try {
            return await infinity?.breakoutRequestGuestToken({
                breakoutUuid,
            });
        } catch (error: unknown) {
            if (!(error instanceof Error)) {
                throw error;
            }
            logger.error(
                {context: 'Meeting', error, breakoutUuid},
                'failed to generate guest token for breakout room',
            );
        }
    };
    const breakoutRequestGuestToken = async (
        breakoutUuid: RoomID,
        onDone?: (token: string) => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            const res = await _breakoutRequestGuestToken(breakoutUuid);
            const token = res?.data.result;

            if (!token) {
                throw new Error('No guest token returned');
            }

            onDone?.(token);
        } catch (error: unknown) {
            onFail?.(error);
        }
    };
    const toggleLayoutOverlayTextEnabled = async () => {
        await _changeLayout({
            enable_overlay_text: !props.layoutOverlayTextEnabled,
        });
    };
    const lock = async (lock: boolean) => {
        await infinity.lock({lock});
    };
    const startConference = async () => {
        await infinity.startConference({});
    };
    const disconnectAll = async () => {
        await infinity.disconnectAll({});
    };
    const askForHelp = async (
        roomId = infinity.roomId,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await infinity.breakoutAskForHelp({breakoutUuid: roomId});
            onDone?.();
        } catch (error: unknown) {
            onFail?.(error);
        }
    };
    const cancelAskingForHelp = async (
        roomId = infinity.roomId,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await infinity.breakoutRemoveAskForHelp({breakoutUuid: roomId});
            onDone?.();
        } catch (error: unknown) {
            onFail?.(error);
        }
    };
    const joinBreakoutRoom = async (
        roomId: RoomID,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await infinity.joinBreakoutRoom({
                ...getCallConfigs(),
                mediaStream: () => mediaService.media?.stream,
                breakoutUuid: infinity.isBreakoutRoom(roomId)
                    ? roomId
                    : undefined,
            });
        } catch (error: unknown) {
            onFail?.(error);
        } finally {
            updateHostReferDetails(undefined);
            onDone?.();
        }
    };
    const closeBreakoutRoom = async (roomId: RoomID) => {
        await infinity.closeBreakoutRoom({breakoutUuid: roomId});
    };
    const closeAllBreakoutRooms = async () => {
        await infinity.closeBreakouts();
    };
    const breakout = infinity.breakout;
    const moveParticipants = infinity.breakoutMoveParticipants;
    const openRooms = async (
        roomDetails: BreakoutRoomDetail[],
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            for (const roomDetail of roomDetails) {
                if (roomDetail.name === MainBreakoutRoomId) {
                    throw new Error('Breakout with name "main" is not allowed');
                }
                await breakout(roomDetail);
            }
        } catch (error: unknown) {
            onFail?.(error);
        } finally {
            onDone?.();
        }
    };
    const mute = infinity.mute;
    const clientMute = infinity.clientMute;
    const guestsCanUnmute = infinity.guestsCanUnmute;
    const muteVideo = infinity.muteVideo;
    const presInMix = async (
        ...args: Parameters<typeof infinity.presInMix>
    ) => {
        if (
            infinity.conferenceFeatureFlags === undefined ||
            infinity.conferenceFeatureFlags.isDirectMedia
        ) {
            return;
        }
        await infinity.presInMix(...args);
    };
    const kick = infinity.kick;
    const admit = infinity.admit;
    const spotlight = infinity.spotlight;
    const fecc = infinity.fecc;
    const isBreakoutRoom = () =>
        infinityService.isBreakoutRoom(infinityService.roomId);
    const setRole = async (...args: Parameters<typeof infinity.setRole>) => {
        if (isBreakoutRoom()) {
            return;
        }
        await infinity.setRole(...args);
    };
    const muteAllGuests = infinity.muteAllGuests;
    const notifyNotAFK = infinity.notifyNotAFK;

    /**
     * Send message to conference or direct message to specific participant
     *
     * @param message - The message
     * @param toParticipantUuid - If provided the message will be sent to this
     * participant, if undefined the message is sent to everyone in the meeting.
     */
    const sendMessage = async (
        message: string,
        toParticipantUuid?: ParticipantID,
    ) => {
        const isDirectMessage = !!toParticipantUuid;
        const me = infinity.getMe();
        if (!me) {
            return;
        }
        const chatMessage = {
            displayName: me.displayName || 'User',
            id: uuid(),
            message,
            at: new Date(),
            type: 'user-message',
            userId: me.uuid,
        } satisfies UserChatMessage;

        if (isDirectMessage) {
            addDirectChatMessage(toParticipantUuid, {
                ...chatMessage,
                pending: true,
            });
        } else {
            updateChatMessages([
                ...props.chatMessages,
                {...chatMessage, pending: true},
            ]);
        }

        const recipientRoomId = toParticipantUuid
            ? (props.participants.getCurrentRoom(toParticipantUuid) ??
              infinity.roomId)
            : undefined;

        const result = await infinity.sendMessage({
            payload: message,
            participantUuid: toParticipantUuid,
            ...(recipientRoomId && {
                breakoutUuid:
                    recipientRoomId === MAIN_ROOM_ID
                        ? undefined
                        : recipientRoomId,
            }),
        });

        const setMsgSuccess = () => {
            const sentMessage = {...chatMessage, at: new Date()};
            if (isDirectMessage) {
                deleteDirectChatMessage(toParticipantUuid, sentMessage.id); // remove the pending direct chat message
                addDirectChatMessage(toParticipantUuid, sentMessage);
            } else {
                const messagesWithoutPending = props.chatMessages.filter(
                    ({id}) => sentMessage.id !== id,
                );
                updateChatMessages([...messagesWithoutPending, sentMessage]);
            }
            const recipient =
                toParticipantUuid && recipientRoomId
                    ? props.participants.getCurrent(
                          toParticipantUuid,
                          recipientRoomId,
                      )
                    : undefined;
            outgoingMessageSignal.emit({
                at: sentMessage.at,
                id: sentMessage.id,
                message: sentMessage.message,
                direct: isDirectMessage,
                recipientId: toParticipantUuid,
                recipientDisplayName: recipient?.displayName,
            });
        };

        if (result) {
            setMsgSuccess();
        } else {
            infinityClientSignals.onRetryQueueFlushed.addOnce(() => {
                setMsgSuccess();
            });
        }
    };
    const enableLiveCaptions = async (
        arg: Parameters<InfinityClient['liveCaptions']>[0],
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => {
        try {
            await infinity.liveCaptions(arg);
            onDone?.();
        } catch (error: unknown) {
            onFail?.(error);
        }
    };

    const setPin = (pin?: string) => infinity.setPin(pin);

    const handleOnTransfer = createSignalHandler(
        infinityClientSignals.onTransfer,
        ({alias, token, callTag, target, breakoutName}) => {
            if (isReceivingAnyMedia(config.get('callType'))) {
                updateSplashScreen(undefined);
            }

            props.participants.transfer(breakoutName ?? alias, target);

            const redirect = (reason: DisconnectReason = 'Transfer') => {
                void end({reason}).then(() =>
                    transferSignal.emit({alias, token, callTag, target}),
                );
            };

            if (target === 'direct' || target === 'transcoded') {
                redirect('DirectMediaTransfer');
            } else if (target === 'conference') {
                redirect();
            } else if (target === 'main' || target === 'breakout') {
                updateGuestTransferDetails({
                    alias,
                    token,
                    callTag,
                    target,
                    breakoutName,
                });
            }
        },
    );

    const showMeetingWithControlInfo = () => {
        const text: string = isSendingPresentation(config.get('callType'))
            ? i18n.t(
                  'meeting.not_receiving_media',
                  'Meeting participants and controls are shown on the left.\nYou can also share your screen from the toolbar.',
              )
            : i18n.t(
                  'meeting.not_receiving_media_not_sending_presentation',
                  'Meeting participants and controls are shown on the left.',
              );
        updateSplashScreen({
            screenKey: 'custom',
            text,
            background: '',
            displayDuration: 0,
        });
        updateStep(MeetingFlow.InMeeting);
    };

    const subscribeEvents = () => {
        const handleOnCallConnected = createSignalHandler(
            infinityClientSignals.onCallConnected,
            () => {
                // We don't want to synchronize the local video mute state when we do ICE restart
                if (props.callStage !== CallStage.EventStreamConnected) {
                    // Initial Sync local video mute state, if undefined we send muteVideo true
                    void muteVideo({
                        muteVideo: mediaService.media?.videoMuted ?? true,
                    });
                }
                if (
                    props.callStage === CallStage.Restarting &&
                    props.presentationStream
                ) {
                    present(props.presentationStream);
                }
                props.callStage = CallStage.Connected;
                if (!isReceivingAnyMedia(config.get('callType'))) {
                    showMeetingWithControlInfo();
                }
            },
        );
        const handleOnRemoteStream = createSignalHandler(
            callSignals.onRemoteStream,
            stream => {
                updateStep(MeetingFlow.InMeeting);
                setRemoteStream(stream);
            },
        );
        const handleOnLayoutOverlayTextEnabled = createSignalHandler(
            infinityClientSignals.onLayoutOverlayTextEnabled,
            updateLayoutOverLayTextEnabled,
        );
        const handleOnConnected = createSignalHandler(
            infinityClientSignals.onConnected,
            async () => {
                props.callStage = CallStage.EventStreamConnected;
                logger.debug({context: 'Meeting'}, 'Event stream connected');
                if (isControlOnly(config.get('callType'))) {
                    showMeetingWithControlInfo();
                } else {
                    await presInMix({state: config.get('preferPresInMix')});
                }
                // Because MCU only use this API to update the participant's state,
                // we always call the API to signal the initial mute state
                await clientMute({
                    mute: mediaService.media?.audioMuted ?? true,
                });
            },
        );
        const handleOnPinRequired = createSignalHandler(
            infinityClientSignals.onPinRequired,
            ({
                hasHostPin,
                hasGuestPin,
            }: ExtractSignalType<
                typeof infinityClientSignals.onPinRequired
            >) => {
                logger.debug(
                    {context: 'Meeting', hasHostPin, hasGuestPin},
                    'Require pin',
                );
                if (hasHostPin && hasGuestPin) {
                    updateStep(MeetingFlow.EnterPin);
                } else {
                    updateStep(MeetingFlow.AreYouHost);
                }

                setPinRequired(hasHostPin && hasGuestPin);
            },
        );
        const handleOnDisplayNameRequired = createSignalHandler(
            infinityClientSignals.onDisplayNameRequired,
            () => {
                updateStep(MeetingFlow.DisplayName);
            },
        );
        const handleOnSplashScreen = createSignalHandler(
            infinityClientSignals.onSplashScreen,
            splashScreen => {
                // those keys should be displayed as notifications
                if (
                    splashScreen?.text &&
                    [
                        'direct_media_escalate',
                        'direct_media_deescalate',
                    ].includes(splashScreen?.screenKey)
                ) {
                    return;
                }
                if (
                    isReceivingAnyMedia(config.get('callType')) ||
                    [
                        'direct_media_welcome',
                        'direct_media_waiting_for_host',
                        'direct_media_other_participants_audio_only',
                        'direct_media_host_left',
                    ].includes(splashScreen?.screenKey)
                ) {
                    updateSplashScreen(splashScreen);
                }
                updateStep(MeetingFlow.InMeeting);
            },
        );
        const handleOnIdp = createSignalHandler(
            infinityClientSignals.onIdp,
            idps => {
                props.idps = idps;
                updateStep(MeetingFlow.Idp);
            },
        );
        const handleOnExtension = createSignalHandler(
            infinityClientSignals.onExtension,
            () => {
                updateStep(MeetingFlow.EnterExtension);
            },
        );
        const handleOnPeerDisconnect = createSignalHandler(
            infinityClientSignals.onPeerDisconnect,
            async () => {
                props.callStage = CallStage.Restarting;
                await controls?.restartCall(callAttrs);
            },
        );
        const handleOnDisconnect = createSignalHandler(
            infinityClientSignals.onDisconnected,
            ({error, errorCode}) => {
                if (applicationConfig.disconnectDestination) {
                    return navigateToPostMeeting(
                        props.conferenceAlias,
                        applicationConfig.disconnectDestination,
                    );
                }
                if (error) {
                    updateInfinityError(error, errorCode);
                } else {
                    navigateToPostMeeting(props.conferenceAlias);
                }
            },
        );
        const handleOnError = createSignalHandler(
            infinityClientSignals.onError,
            ({error, errorCode}) => {
                if (error === 'Invalid PIN') {
                    props.error = {
                        type: error,
                        code: i18n.t('pin.invalid', 'Invalid PIN'),
                    };
                    invalidPinSignal.emit();
                    updateStep(
                        props.joinAsHost
                            ? MeetingFlow.EnterHostPin
                            : MeetingFlow.EnterPin,
                    );
                } else {
                    if (props.callStage < CallStage.Connected) {
                        try {
                            addCallRecent({succeeded: false});
                        } catch (error) {
                            logger.error(
                                {error},
                                'Failed to add call to recents',
                            );
                        }
                    }
                    updateInfinityError(error, errorCode);
                }
            },
        );

        const handleOnRequestedLayout = createSignalHandler(
            infinityClientSignals.onRequestedLayout,
            layout => {
                props.currentHostLayout = layout.primaryScreen.hostLayout;
            },
        );

        const handleOnLayout = createSignalHandler(
            infinityClientSignals.onLayoutUpdate,
            layout => {
                props.isPresentationInMixActive = !!layout.pres_slot_coords;
            },
        );

        const handleOnChatMessage = createSignalHandler(
            infinityClientSignals.onMessage,
            message => {
                if (message.message.length > CHARACTER_LIMIT) {
                    logger.warn(
                        `Message received from the server is too big. Length: ${message.message.length}`,
                    );
                    return;
                }

                const chatMessage: UserChatMessage = {
                    ...message,
                    displayName: message.displayName || 'User',
                    type: 'user-message',
                };

                chatMessageSignal.emit(chatMessage);

                if (message.direct) {
                    addDirectChatMessage(message.userId, chatMessage);
                    addUnreadDirectChatMessage(message.userId, chatMessage);
                } else {
                    updateChatMessages([...props.chatMessages, chatMessage]);
                    updateUnreadChatMessages([
                        ...props.unreadChatMessages,
                        chatMessage,
                    ]);
                }
            },
        );

        const handleOnBreakoutRefer = createSignalHandler(
            infinityClientSignals.onBreakoutRefer,
            referDetails => {
                updateChatMessages([]);

                const mainMe = infinity.getMe('main');
                if (!mainMe) {
                    return;
                }
                if (!referDetails.requesterUuid) {
                    void joinBreakoutRoom(referDetails.breakoutUuid ?? 'main');
                    return;
                }
                if (mainMe.uuid === referDetails.requesterUuid) {
                    void joinBreakoutRoom(referDetails.breakoutUuid);
                    return;
                }
                updateHostReferDetails(referDetails);
            },
        );

        const handleOnBreakoutEnd = createSignalHandler(
            infinityClientSignals.onBreakoutEnd,
            ({breakout_uuid}) => {
                updateBreakoutsAskingForHelp(
                    props.breakoutsAskingForHelp.filter(
                        room => room.roomId !== breakout_uuid,
                    ),
                );
            },
        );

        const handleOnConferenceStatus = createSignalHandler(
            infinityClientSignals.onConferenceStatus,
            ({id, status}) => {
                if (!status?.breakout || !infinity.getMe('main')?.isHost) {
                    return;
                }

                const isAskingForHelp = status.breakoutbuzz?.value ?? false;
                const isAlreadyAskingForHelp =
                    props.breakoutsAskingForHelp.some(
                        ({roomId}) => roomId === id,
                    );

                if (!isAskingForHelp) {
                    if (isAlreadyAskingForHelp) {
                        updateBreakoutsAskingForHelp(
                            props.breakoutsAskingForHelp.filter(
                                ({roomId}) => roomId !== id,
                            ),
                        );
                        breakoutHelpCancelledSignal.emit(id);
                    }
                    return;
                }

                const isNewRequest = !isAlreadyAskingForHelp;
                const newStatus: BreakoutAskingForHelpStatus = {
                    roomId: id,
                    breakoutName: status.breakoutName ?? id,
                    askForHelpTime: status.breakoutbuzz?.time ?? 0,
                };
                const newState = isAlreadyAskingForHelp
                    ? props.breakoutsAskingForHelp.map(s =>
                          s.roomId === id ? newStatus : s,
                      )
                    : [...props.breakoutsAskingForHelp, newStatus];
                updateBreakoutsAskingForHelp(
                    newState.sort(
                        (a, b) => a.askForHelpTime - b.askForHelpTime,
                    ),
                );
                if (isNewRequest) {
                    breakoutHelpRequestedSignal.emit(id);
                }
            },
        );

        const handleAudioMute = createDebounceMute(
            {timerId: 0, muted: undefined},
            muted => {
                void clientMute({mute: muted});
                const me = infinity.getMe();
                if (
                    !muted &&
                    me?.isMuted &&
                    (me?.isHost ||
                        infinity.conferenceStatus.get(infinity.roomId)
                            ?.guestsCanUnmute)
                ) {
                    void mute({mute: false});
                }
            },
        );
        const handleVideoMute = createDebounceMute(
            {timerId: 0, muted: undefined},
            muted => {
                void muteVideo({muteVideo: muted});
            },
        );

        const handleAddTrack = createSignalHandler(
            mediaSignals.onAddTrack,
            async track => {
                // A restored track only emits `onMediaTrackMuted` when it is
                // muted, so the server state has to be reconciled here.
                if (props.callStage < CallStage.EventStreamConnected) {
                    return;
                }
                switch (track.kind) {
                    case 'audio': {
                        await clientMute({
                            mute: mediaService.media?.audioMuted ?? true,
                        });
                        break;
                    }
                    case 'video': {
                        await muteVideo({
                            muteVideo: mediaService.media?.videoMuted ?? true,
                        });
                        break;
                    }
                    default:
                        break;
                }
            },
        );

        const handleTrackMuted = createSignalHandler(
            mediaSignals.onMediaTrackMuted,
            track => {
                // Only handle the final track, ignore intermediate tracks
                if (!mediaService.media?.isCurrentTrack(track)) {
                    return;
                }
                switch (track.kind) {
                    case 'audioinput': {
                        handleAudioMute(track);
                        break;
                    }
                    case 'videoinput': {
                        handleVideoMute(track);
                        break;
                    }
                    default:
                        break;
                }
            },
        );

        const handlePresentationStream = createSignalHandler(
            presentationStreamSignal,
            stream => {
                props.presentationStream = stream;
                if (
                    props.presentationStream?.getAudioTracks().length &&
                    mediaService.media?.audioMuted
                ) {
                    void mute({
                        mute: false,
                    });
                }
            },
        );

        const handleOnRaiseHand = createSignalHandler(
            infinityClientSignals.onRaiseHand,
            ({participant}) => {
                participant.displayName =
                    participant.overlayText || participant.displayName;
            },
        );

        const handleOnLiveCaptions = createSignalHandler(
            infinityClientSignals.onLiveCaptions,
            ({data: text, isFinal, speakers}) => {
                const liveCaption = {
                    text,
                    isFinal,
                    at: new Date(),
                    speakers,
                };

                if (
                    props.liveCaptions.length > 0 &&
                    !props.liveCaptions[props.liveCaptions.length - 1]?.isFinal
                ) {
                    if (
                        liveCaption.isFinal ||
                        liveCaption.text.length >=
                            (props.liveCaptions[props.liveCaptions.length - 1]
                                ?.text.length ?? 0)
                    ) {
                        props.liveCaptions[props.liveCaptions.length - 1] =
                            liveCaption;
                        props.liveCaptions = [...props.liveCaptions];
                    }
                } else {
                    props.liveCaptions = [...props.liveCaptions, liveCaption];
                }

                liveCaptionsSignal.emit();
            },
        );

        const addCallRecent = ({
            succeeded,
            conferenceName,
        }: {
            succeeded: boolean;
            conferenceName?: string;
        }) => {
            if (!applicationConfig.callHistory.enableRecents) {
                return;
            }
            const incomingCall = callAttrs.token
                ? registrationService.getIncomingCall(callAttrs.token)
                : undefined;
            callHistoryService.addRecent(
                toCallRecent({
                    succeeded,
                    conferenceName,
                    conferenceAlias: callAttrs.conferenceAlias,
                    serviceType: infinity.serviceType,
                    incomingCall,
                }),
            );
        };

        // Only called when EventSource is connected from the very beginning
        const handleOnAuthenticatedWithConference = createSignalHandler(
            infinityClientSignals.onAuthenticatedWithConference,
            async ({conferenceName}) => {
                try {
                    addCallRecent({succeeded: true, conferenceName});
                } catch (error) {
                    logger.error({error}, 'Failed to add call to recents');
                }

                // Because MCU only use this API to update the participant's state,
                // we always call the API to signal the initial mute state
                // Also see here https://github.com/pexip/mcu/issues/40796
                await muteVideo({
                    muteVideo: mediaService.media?.videoMuted ?? true,
                });
            },
        );

        const hasQualityStatsChanged = (
            last: Stats | undefined,
            current: Stats,
        ) => {
            if (!last) {
                return true;
            }

            if (
                last?.outbound?.video?.qualityLimitationReason !==
                current?.outbound?.video?.qualityLimitationReason
            ) {
                return true;
            }

            const lastHighFpsVolatility =
                Number(last?.outbound?.video?.fpsVolatility) > 10;
            const highFpsVolatility =
                Number(current?.outbound?.video?.fpsVolatility) > 10;
            if (lastHighFpsVolatility !== highFpsVolatility) {
                return true;
            }

            return false;
        };

        const logStats = (stats: Stats) => {
            logger.info(
                {stats, backgroundEffects: config.get('segmentationEffects')},
                'Quality stats changed',
            );
            if (applicationConfig.sendCallStatisticsEvents) {
                infinity.pushStatistics();
            }
        };

        const handleRtcStats = (stats: Stats) => {
            const lastStats = window.pexDebug?.stats as Stats;

            if (hasQualityStatsChanged(lastStats, stats)) {
                logStats(stats);
            }

            window.pexDebug = {...window.pexDebug, stats};
        };

        const handleCallQualityChanged = () => {
            const lastStats = window.pexDebug?.stats as Stats;
            lastStats && logStats(lastStats);
        };

        const handlePresoContentHintChanged = () => {
            // Update degradationPreference
            infinity.setDegradationPreference({
                presentation: deriveDegradationPreference(),
            });
        };

        props.detachSignals = [
            infinityClientSignals.onAuthenticatedWithConference.add(
                handleOnAuthenticatedWithConference,
            ),
            mediaSignals.onMediaTrackMuted.add(handleTrackMuted),
            mediaSignals.onAddTrack.add(handleAddTrack),
            presentationStreamSignal.add(handlePresentationStream),
            callSignals.onCallConnected.add(handleOnCallConnected),
            callSignals.onRemoteStream.add(handleOnRemoteStream),
            infinityClientSignals.onConnected.add(handleOnConnected),
            infinityClientSignals.onLayoutOverlayTextEnabled.add(
                handleOnLayoutOverlayTextEnabled,
            ),
            infinityClientSignals.onPinRequired.add(handleOnPinRequired),
            infinityClientSignals.onDisplayNameRequired.add(
                handleOnDisplayNameRequired,
            ),
            infinityClientSignals.onSplashScreen.add(handleOnSplashScreen),
            infinityClientSignals.onIdp.add(handleOnIdp),
            infinityClientSignals.onExtension.add(handleOnExtension),
            callSignals.onCallQuality.add(handleCallQualityChanged),
            callSignals.onRtcStats.add(handleRtcStats),
            subscribePageHide(() => {
                void end({reason: 'Browser closed'});
            }),
            infinityClientSignals.onPeerDisconnect.add(handleOnPeerDisconnect),
            infinityClientSignals.onDisconnected.add(handleOnDisconnect),
            infinityClientSignals.onError.add(handleOnError),
            config.subscribe('preferPresInMix', preferPresInMix => {
                if (props.callStage >= CallStage.EventStreamConnected) {
                    void presInMix({state: preferPresInMix});
                }
            }),
            config.subscribe('presoContentHint', handlePresoContentHintChanged),
            infinityClientSignals.onRequestedLayout.add(
                handleOnRequestedLayout,
            ),
            infinityClientSignals.onLayoutUpdate.add(handleOnLayout),
            infinityClientSignals.onMessage.add(handleOnChatMessage),
            infinityClientSignals.onBreakoutRefer.add(handleOnBreakoutRefer),
            infinityClientSignals.onRaiseHand.add(handleOnRaiseHand),
            infinityClientSignals.onLiveCaptions.add(handleOnLiveCaptions),
            infinityClientSignals.onBreakoutEnd.add(handleOnBreakoutEnd),
            infinityClientSignals.onConferenceStatus.add(
                handleOnConferenceStatus,
            ),
            infinityClientSignals.onCancelTransfer.add(() => {
                updateGuestTransferDetails(undefined);
                updateHostReferDetails(undefined);
            }),
        ];
    };

    const getCurrentRoomFeccParticipantIDs = memoize(
        () => {
            const feccEnabledPids = props.participants.get({
                filterBy: GroupKey.FECC,
                roomId: infinity.roomId,
            });
            const pids: ParticipantID[] = [];
            for (const pid of props.currentFeccPids.get()) {
                if (feccEnabledPids.has(pid)) {
                    pids.push(pid);
                }
            }
            return pids;
        },
        () => [
            props.participants.get({
                filterBy: GroupKey.FECC,
                roomId: infinity.roomId,
            }),
            props.currentFeccPids.changed,
        ],
    );

    if (callAttrs.conferenceAlias) {
        subscribeEvents();
    }

    return {
        getSearchQuery: () => props.searchQuery,
        setSearchQuery: updateSearchQuery,
        getMe: (roomId?: RoomID) => infinity.getMe(roomId),
        ableToPresent: () => {
            return (
                isSendingPresentation(config.get('callType')) &&
                Boolean(
                    navigator?.mediaDevices &&
                    'getDisplayMedia' in navigator.mediaDevices &&
                    infinityService.conferenceFeatureFlags?.callType ===
                        'video',
                )
            );
        },
        ableToReceivePresentation: () => {
            return isReceivingPresentation(config.get('callType'));
        },
        allowedToPresent: (roomId?: RoomID) => {
            const status = roomId
                ? infinityService.conferenceStatus.get(roomId)
                : undefined;
            const guestsCanPresent = status
                ? status.guestsCanPresent
                : infinityService.conferenceFeatureFlags?.guestsCanPresent;

            const {canControl, isPresenting} = infinity.getMe(roomId) ?? {};

            return isAllowedToPresent({
                canControl,
                guestsCanPresent,
                isPresenting,
            });
        },
        getChatEnabled: (roomId?: RoomID) => {
            const status = roomId
                ? infinityService.conferenceStatus.get(roomId)
                : undefined;

            if (status && typeof status.chatEnabled !== 'undefined') {
                return status.chatEnabled;
            }

            return infinityService.conferenceFeatureFlags?.chatEnabled ?? false;
        },
        getRooms: () => infinity.rooms,
        getStep: () => props.step,
        setStep: updateStep,
        getSplashScreen: () => props.splashScreen,
        setSplashScreen: updateSplashScreen,
        getBreakoutsAskingForHelp: () => props.breakoutsAskingForHelp,
        getGuestTransferDetails: () => props.guestTransferDetails,
        getHostReferDetails: () => props.hostReferDetails,
        getIdps: () => props.idps,
        setIdps: updateIdp,
        getLiveCaptionsEnabled: () => props.liveCaptionsEnabled,
        setLiveCaptionsEnabled: uppdateLiveCaptionsEnabled,
        getSelectedCaptionsLanguage: () => props.selectedCaptionsLanguage,
        setSelectedCaptionsLanguage: updateSelectedCaptionsLanguage,
        getIsTogglingLiveCaptions: () => props.isTogglingLiveCaptions,
        toggleLiveCaptions,
        getChatMessages: () => props.chatMessages,
        setChatMessages: updateChatMessages,
        getUnreadChatMessages: () => props.unreadChatMessages,
        setUnreadChatMessages: updateUnreadChatMessages,
        getDirectChatMessages: () => props.directChatMessages,
        getUnreadDirectChatMessages: () => props.unreadDirectChatMessages,
        deleteUnreadDirectChatMessages:
            deleteUnreadDirectChatMessagesWithParticipant,
        getUnseenUnreadDirectChatMessages: () =>
            props.unseenUnreadDirectChatMessages,
        setUnseenDirectChatMessagesToSeen:
            deleteAllUnseenUnreadDirectChatMessages,
        getLayoutOverlayEnabled: () => props.layoutOverlayTextEnabled,
        getRemoteStream: () => props.remoteStream,
        getRoomId: () => infinity.roomId,
        getConferenceName: () => infinity.conferenceName,
        getConferenceStatus: () => infinity.conferenceStatus,
        getConferenceFeatureFlags: () => infinity.conferenceFeatureFlags,
        getPinRequired: () => props.pinRequired,
        getCurrentHostLayout: () => props.currentHostLayout,
        getParticipants: ({
            roomId = infinity.roomId,
            ...rest
        }: Parameters<typeof props.participants.get>[0] = {}) =>
            props.participants.get({roomId, ...rest}),
        getAllParticipantsAndActivities: () => props.participants.dump(),
        getSupportedBreakoutParticipants: ({
            roomId = infinity.roomId,
            ...rest
        }: Parameters<typeof props.participants.get>[0] = {}) => {
            return props.participants.get({
                roomId,
                filterBy: GroupKey.Breakout,
                ...rest,
            });
        },
        getParticipant: props.participants.getCurrent,
        consumeParticipantActivities: props.participants.consumeActivities,
        hasParticipantActivities: props.participants.hasActivities,
        getDisconnectedParticipant: props.participants.getDisconnected,
        isParticipantDisconnected: props.participants.isDisconnected,
        getParticipantRoomID: (participantUuid?: ParticipantID) => {
            const uuid = participantUuid ?? infinity.getMe()?.uuid;
            if (!uuid) {
                return undefined;
            }
            return props.participants.getCurrentRoom(uuid);
        },
        getBreakoutRooms: () => infinity.breakoutRooms,
        getBreakoutRoomNames: () => infinity.breakoutRoomNames,
        getSecureCheckCode: () => infinity.secureCheckCode,
        getLiveCaptionsAvailability: () => {
            const status = infinity.conferenceStatus.get(infinity.roomId);
            return !!status?.liveCaptionsAvailable;
        },
        getAvailableCaptionsLanguages: () => {
            const status = infinity.conferenceStatus.get(infinity.roomId);
            return status?.liveCaptionsAvailableLanguages;
        },
        getSourceCaptionsLanguages: () => {
            const status = infinity.conferenceStatus.get(infinity.roomId);
            return status?.liveCaptionsSourceLanguages;
        },
        getIsFeccEnabled: () => infinity.isFeccEnabled,
        getCurrentRoomFeccParticipantIDs,
        getCurrentFeccPidsSignalState: () => props.currentFeccPids,
        addCurrentRoomFeccParticipantID: (pid: ParticipantID) => {
            const set = props.currentFeccPids.get();
            set.add(pid);
            props.currentFeccPids.set(set);
        },
        removeCurrentRoomFeccParticipantID: (pid: ParticipantID) => {
            const set = props.currentFeccPids.get();
            set.delete(pid);
            props.currentFeccPids.set(set);
        },
        getConferenceAlias: () => props.conferenceAlias,
        getRemoteAlias: () => {
            const incomingCall = callAttrs.token
                ? registrationService.getIncomingCall(callAttrs.token)
                : undefined;
            return incomingCall?.conference_alias === props.conferenceAlias
                ? incomingCall.remote_alias
                : undefined;
        },
        getEndedReason: () => props.endedReason,
        getCallStage: () => props.callStage,
        getLiveCaptions: () => props.liveCaptions,
        getIsPresentationInMixActive: () => props.isPresentationInMixActive,
        isBreakoutRoom,
        release,
        continueCall: toFn(continueCall),
        end: toFn(end),
        leave: toFn(leave),
        present,
        endPresent,
        raiseHand: toFn(raiseHand),
        lowerAllRaisedHands: toFn(lowerAllRaisedHands),
        changeLayout: toFn(changeLayout),
        changePersonalLayout: toFn(changePersonalLayout),
        resetToMeetingLayout: toFn(resetToMeetingLayout),
        toggleLayoutOverlayTextEnabled: toFn(toggleLayoutOverlayTextEnabled),
        lock: toFn(lock),
        startConference: toFn(startConference),
        disconnectAll: toFn(disconnectAll),
        askForHelp: toFn(askForHelp),
        cancelAskingForHelp: toFn(cancelAskingForHelp),
        joinBreakoutRoom: toFn(joinBreakoutRoom),
        closeBreakoutRoom: toFn(closeBreakoutRoom),
        closeAllBreakoutRooms: toFn(closeAllBreakoutRooms),
        breakoutRequestGuestToken: toFn(breakoutRequestGuestToken),
        breakout: toFn(breakout),
        moveParticipants: toFn(moveParticipants),
        openRooms: toFn(openRooms),
        mute: toFn(mute),
        muteVideo: toFn(muteVideo),
        kick: toFn(kick),
        admit: toFn(admit),
        fecc: toFn(fecc),
        spotlight: toFn(spotlight),
        setRole: toFn(setRole),
        enableLiveCaptions: toFn(enableLiveCaptions),
        presInMix: toFn(presInMix),
        muteAllGuests: toFn(muteAllGuests),
        guestsCanUnmute: toFn(guestsCanUnmute),
        notifyNotAFK: toFn(notifyNotAFK),
        sendMessage: toFn(sendMessage),
        setPin,
        joinAsHost: () => {
            props.joinAsHost = true;
            updateStep(MeetingFlow.EnterHostPin);
        },
        joinAsGuest: () => {
            props.joinAsHost = false;
            void continueCall({pin: 'none'});
        },
        getError: () => props.error,
        transfer: handleOnTransfer,
        removeError: () => {
            if (!props.error) {
                return;
            }
            const errorType = props.error.type;
            props.error = undefined;
            switch (errorType) {
                case 'Invalid PIN': {
                    invalidPinSignal.emit();
                    break;
                }
                default:
                    break;
            }
        },
        joinWithDisplayName: (displayName: string) => {
            if (displayName.length) {
                config.set({
                    key: 'displayName',
                    value: displayName,
                    persist: true,
                });
                // Session only, the persisted preference is owned by the settings
                config.set({key: 'skipDisplayNamePrompt', value: true});
                void continueCall({});
            }
        },
    };
};

export type Meeting = ReturnType<typeof createMeeting>;

export type DeleteUnreadDirectChatMessages =
    Meeting['deleteUnreadDirectChatMessages'];

interface Props {
    infinityClient: InfinityClient;
    meeting: ReturnType<typeof createMeeting>;
    prevMeetingAttrs: Partial<MeetingAttributes>;
}

export const infinityService = createInfinityClient(
    infinityClientSignals,
    callSignals,
);

const NO_CALL = '';

export const createInfinity = () => {
    const props: Props = {
        infinityClient: infinityService,
        prevMeetingAttrs: {},
        meeting: createMeeting({
            infinity: infinityService,
            callAttrs: {conferenceAlias: NO_CALL},
            controls: undefined,
        }),
    };

    let detachCallConnected: Detach | undefined;
    let transferSubscription: Detach | undefined;

    const unsubscribeTransfer = () => {
        transferSubscription?.();
        transferSubscription = undefined;
    };

    const handleError = (error: unknown) => {
        if (!(error instanceof Error)) {
            throw error;
        }
        logger.error(error);
        // Send empty error since we don't want to expose the error message to the user
        infinityErrorSignal.emit({type: '\n', code: ''});
    };

    const handleTransfer = (
        event: GetSignalType<(typeof infinityClientSignals)['onTransfer']>,
    ) => {
        switch (event.target) {
            case 'direct':
            case 'transcoded': {
                const {participants, activities} =
                    props.meeting.getAllParticipantsAndActivities();
                props.prevMeetingAttrs = {
                    liveCaptionsEnabled: props.meeting.getLiveCaptionsEnabled(),
                    chatMessages: props.meeting.getChatMessages(),
                    unreadChatMessages: props.meeting.getUnreadChatMessages(),
                    directChatMessages: props.meeting.getDirectChatMessages(),
                    unreadDirectChatMessages:
                        props.meeting.getUnreadDirectChatMessages(),
                    unseenUnreadDirectChatMessages:
                        props.meeting.getUnseenUnreadDirectChatMessages(),
                    previousParticipants: participants,
                    previousParticipantActivities: activities,
                };
                break;
            }
            default:
                props.prevMeetingAttrs = {};
                break;
        }
        props.meeting.transfer(event);
    };

    const call = async (
        callArgs: CallArgs,
        presentationStream?: MediaStream,
    ) => {
        try {
            props.meeting.release();
            props.meeting = createMeeting({
                infinity: props.infinityClient,
                callAttrs: {
                    conferenceAlias: callArgs.conferenceAlias,
                    callTag: callArgs.callTag,
                    conferenceExtension: callArgs.conferenceExtension,
                    token: callArgs.token,
                },
                meetingAttrs: props.prevMeetingAttrs,
                controls: {
                    call: async (args?: Partial<CallArgs>) => {
                        const opts = {
                            ...getCallConfigs(),
                            ...callArgs,
                            ...args,
                        };
                        await props.infinityClient.call(opts);
                    },
                    endCall: end,
                    restartCall: async args => {
                        await props.infinityClient.restartCall({
                            ...getCallConfigs(),
                            ...callArgs,
                            ...args,
                        });
                    },
                    changeLayout: async transforms => {
                        const result = await props.infinityClient.setLayout({
                            transforms,
                        });
                        return result?.status === 200;
                    },
                    changePersonalLayout: async transforms => {
                        const result =
                            await props.infinityClient.setPersonalLayout({
                                transforms,
                            });
                        return result?.status === 200;
                    },
                },
            });
            props.prevMeetingAttrs = {};
            meetingSignal.emit();

            if (presentationStream) {
                detachCallConnected = callSignals.onCallConnected.addOnce(
                    () => {
                        startPresentationSignal.emit({
                            stream: presentationStream,
                        });
                        detachCallConnected = undefined;
                    },
                );
            }

            unsubscribeTransfer();
            transferSubscription =
                infinityClientSignals.onTransfer.add(handleTransfer);
            await props.infinityClient.call({
                ...getCallConfigs(),
                ...callArgs,
            });
        } catch (error: unknown) {
            handleError(error);
        }
    };

    const end = async (args: EndArgs) => {
        try {
            await props.infinityClient.disconnect(args);
        } catch (error: unknown) {
            handleError(error);
        }
    };

    return Object.freeze({
        getMe: (roomId?: RoomID) => {
            return props.meeting.getMe(roomId);
        },
        getMeeting: () => props.meeting,
        getServiceType: () => props.infinityClient.serviceType,
        call: (callArgs: CallArgs, presentationStream?: MediaStream) => {
            void call(callArgs, presentationStream);
        },
        endCall: () => {
            detachCallConnected?.();
            props.meeting.end();
            unsubscribeTransfer();
        },
        setBandwidth: (bandwidth: number) => {
            logger.info({context: 'Meeting', bandwidth}, 'update bandwidth');
            props.infinityClient.setBandwidth(bandwidth);
        },
        setStream: (stream: MediaStream) => {
            logger.info({context: 'Meeting', stream}, 'update stream');
            props.infinityClient.setStream(stream);
        },
    });
};

export const infinity = createInfinity();

export type Infinity = ReturnType<typeof createInfinity>;
